import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import MainTabs from '@/karehero-app/shell/MainTabs';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

// Kinde owns the words on its own pages, and there is no way to read them short
// of running a real reset: nothing in main or llama carries them, because the
// app never renders them. So the states below show where the message lands and
// say whose it is, rather than inventing a sentence that would get quoted back
// as Kinde's.
const KINDE_COPY_PENDING = 'Kinde’s message, still to capture';

const welcome = (
  <WelcomeScreen
    onCreateAccount={noop}
    onSignIn={noop}
    onForgotPassword={noop}
  />
);

// The sheet over the welcome screen, on whichever of Kinde's pages the step is.
const reset = (initialStep: 'form' | 'code' | 'password', error?: string) => (
  <>
    {welcome}
    <KindeHostedSheet
      variant='reset'
      initialStep={initialStep}
      error={error}
      onClose={noop}
    />
  </>
);

// Forgot password, on its own page under Sign up.
//
// Almost none of this is ours to design, and that is the finding rather than a
// gap: main authenticates entirely through the Kinde SDK (`login`, `register`,
// `logout` in front-end/pwa/src/hooks/useAuth.ts) and has no password screen of
// its own, so resetting a password happens on Kinde's hosted pages inside the
// system browser sheet. What we own is the way in, the way back, and where a
// reset lands.
export const forgotPasswordSpec: HandoverSpec = {
  id: 'forgot-password',
  title: 'Forgot password',
  description: '',
  links: [],
  flows: [
    {
      title: 'Forgot password to home',
      steps: [
        {
          id: 'welcome',
          title: 'Welcome',
          tap: '‘Forgot password?’',
          render: () => welcome,
        },
        {
          id: 'reset-email',
          title: 'Reset your password (Kinde)',
          tap: 'Enter the address, tap ‘Continue’',
          render: () => reset('form'),
        },
        {
          id: 'reset-code',
          title: 'Check your inbox (Kinde)',
          tap: 'Enter the emailed code',
          render: () => reset('code'),
        },
        {
          id: 'reset-password',
          title: 'Set your password (Kinde)',
          tap: 'Tap ‘Continue’',
          render: () => reset('password'),
        },
        {
          // A reset signs them in, so there is no second trip through the
          // welcome screen and nothing to verify: the account already has its
          // access. It lands where signing in lands.
          id: 'home',
          title: 'Straight into the app',
          tap: 'Kinde signs them in',
          render: () => <MainTabs tab='home' />,
        },
      ],
    },
  ],
  states: [
    {
      id: 'wrong-code',
      title: 'The code is wrong',
      kind: 'error',
      row: 'On Kinde’s pages',
      repro:
        'Kinde rejects it under the field. Seeing the real line needs a live reset',
      render: () => reset('code', KINDE_COPY_PENDING),
    },
    {
      id: 'expired-code',
      title: 'The code has expired',
      kind: 'error',
      row: 'On Kinde’s pages',
      repro:
        'Same page, and Resend code is the way out of it. Kinde’s copy again',
      render: () => reset('code', KINDE_COPY_PENDING),
    },
    {
      id: 'short-password',
      title: 'The new password is too short',
      kind: 'error',
      row: 'On Kinde’s pages',
      repro: 'Under eight characters. The rule is on the page before it fails',
      render: () => reset('password', KINDE_COPY_PENDING),
    },
    {
      // The one failure on our side of the sheet, and the only one we can write
      // the copy for.
      id: 'cancelled',
      title: 'They back out of the sheet',
      kind: 'error',
      row: 'Back in the app',
      repro:
        'Cancel on the browser chrome. Back on the welcome screen, still signed out, password unchanged',
      render: () => welcome,
    },
  ],
  components: {},
};
